import haptic from '@/common/utils/haptics';
import { CreditCard } from 'lucide-react';
import Text from '@/common/components/atom/text';
import clsx from 'clsx';
import { useState, useEffect, useId } from 'react';

interface PaymentMethodsProps {
  paymentMethods: any[];
  additionalContent: string;
  selectedPaymentMethod?: string;
  onSelectionChange?: (paymentMethod: string) => void;
  /** payment_method keys that stay visible but cannot be selected */
  disabledPaymentMethods?: string[];
}

export const PaymentMethods = (props: PaymentMethodsProps) => {
  const { paymentMethods, additionalContent, selectedPaymentMethod, onSelectionChange, disabledPaymentMethods = [] } = props;
  const [selectedMethod, setSelectedMethod] = useState(selectedPaymentMethod || paymentMethods?.[0]?.payment_method);
  const radioGroupName = useId();
  const disabledSet = new Set(disabledPaymentMethods.map(m => String(m).toLowerCase()));

  useEffect(() => {
    if (selectedPaymentMethod) {
      setSelectedMethod(selectedPaymentMethod);
    } else if (paymentMethods?.[0]?.payment_method && !selectedMethod) {
      setSelectedMethod(paymentMethods[0].payment_method);
    }
  }, [selectedPaymentMethod, paymentMethods, selectedMethod]);

  const isDisabled = (method: string) => disabledSet.has(String(method || '').toLowerCase());

  const bankMethods = paymentMethods.filter(
    item =>
      /^(saman|mellat|parsian|pasargad|saderat|sep|sadad|zarinpal)(?:_topups(?:\+\d+)?)?$/i.test(item.payment_method) ||
      String(item.title).includes('درگاه'),
  );
  const preferredBank =
    bankMethods.find(item => /^saman(?:_topups(?:\+\d+)?)?$/i.test(item.payment_method) && !isDisabled(item.payment_method)) ||
    bankMethods.find(item => !isDisabled(item.payment_method));
  const bankSelected = bankMethods.some(item => item.payment_method === selectedMethod);
  const visibleMethods = [
    ...(bankMethods.length
      ? [{ ...preferredBank, payment_method: '__bank_group', title: 'پرداخت آنلاین با کارت بانکی', logo: '', html_content: '' }]
      : []),
    ...paymentMethods.filter(item => !bankMethods.includes(item)),
  ];

  const handleSelect = (method: string) => {
    if (isDisabled(method)) return;
    if (method !== selectedMethod) haptic('selection');
    setSelectedMethod(method);
    onSelectionChange?.(method);
  };

  return (
    <section aria-label="روش پرداخت" className="px-5 py-4 bg-white md:rounded-lg shadow-card">
      <h2 className="mb-2 text-sm font-bold">روش پرداخت</h2>
      <div className="flex flex-col divide-y divide-slate-100">
        {visibleMethods.map(item => {
          const isBankGroup = item.payment_method === '__bank_group';
          const targetMethod = isBankGroup ? preferredBank?.payment_method : item.payment_method;
          const disabled = !targetMethod || isDisabled(targetMethod);
          const selected = isBankGroup ? bankSelected : selectedMethod === item.payment_method;
          return (
            <label
              key={item.payment_method}
              aria-disabled={disabled}
              className={clsx('relative flex flex-col bg-white text-right', {
                'cursor-pointer': !disabled,
                'min-h-[100px]': isBankGroup,
                'min-h-[64px]': !isBankGroup,
                'cursor-not-allowed opacity-50': disabled,
              })}
            >
              <input
                type="radio"
                name={radioGroupName}
                value={targetMethod || ''}
                checked={selected && !disabled}
                disabled={disabled}
                onChange={() => targetMethod && handleSelect(targetMethod)}
                className="sr-only"
                aria-label={item.title}
              />
              <div className="flex items-start justify-between gap-4 py-3">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  {isBankGroup && <CreditCard size={26} strokeWidth={1.8} className="shrink-0 text-black" />}
                  {item.logo && (
                    <div className="flex-shrink-0 w-7 h-7 flex items-center justify-center">
                      <img src={item.logo} alt={item.title} className="w-full h-full object-contain" />
                    </div>
                  )}
                  <div className="w-full flex flex-col gap-1 min-w-0">
                    <Text fontWeight="semiBold" fontSize="sm" className={clsx('text-slate-900', { '!text-[15px]': isBankGroup })}>
                      {item.title}
                    </Text>
                    {isBankGroup && (
                      <div className="flex flex-wrap items-center gap-2 pt-2">
                        {bankMethods
                          
                          .map(bank => (
                            <button
                              key={bank.payment_method}
                              type="button"
                              disabled={isDisabled(bank.payment_method)}
                              aria-label={bank.title}
                              aria-pressed={selectedMethod === bank.payment_method}
                              onClick={event => {
                                event.preventDefault();
                                event.stopPropagation();
                                handleSelect(bank.payment_method);
                              }}
                              className={clsx('flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-lg border-[1.5px] outline-none', {
                                'border-primary bg-slate-50 text-primary': selectedMethod === bank.payment_method,
                                'border-transparent bg-slate-50 text-slate-500': selectedMethod !== bank.payment_method,
                                'opacity-40': isDisabled(bank.payment_method),
                              })}
                            >
                              {bank.logo ? <img src={bank.logo} alt={bank.title} className="h-7 w-7 object-contain" /> : <span className="text-xs font-medium">{bank.title.replace('درگاه پرداخت ', '')}</span>}
                            </button>
                          ))}
                      </div>
                    )}
                    {/^snapp(?:_topups(?:\+\d+)?)?$/i.test(item.payment_method) && (
                      <span className="text-xs text-slate-500">الان ویزیت شو، بعداً پرداخت کن</span>
                    )}
                    {item.html_content && !/^snapp(?:_topups(?:\\+\\d+)?)?$/i.test(item.payment_method) && (
                      <div className="w-full text-xs text-slate-500 [&>p]:m-0" dangerouslySetInnerHTML={{ __html: item.html_content }} />
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-3 flex-shrink-0 pt-0.5">
                  {item.display_amount && (
                    <Text
                      fontWeight="medium"
                      fontSize="sm"
                      className={clsx({
                        'text-green-600': selected && !disabled,
                        'text-slate-500': !selected || disabled,
                      })}
                    >
                      {item.display_amount}
                    </Text>
                  )}
                  <div
                    className={clsx('relative grid place-items-center shrink-0 w-5 h-5 rounded-full border-2', {
                      'border-primary bg-primary': selected && !disabled,
                      'border-slate-300 bg-white': !selected || disabled,
                    })}
                  >
                    {selected && !disabled && <span className="block w-2 h-2 rounded-full bg-white" />}
                  </div>
                </div>
              </div>
              {item.additional_html && (
                <div className="w-full text-slate-600 [&>p]:m-0" dangerouslySetInnerHTML={{ __html: item.additional_html }} />
              )}
            </label>
          );
        })}
        {additionalContent && <div className="w-full text-slate-600 [&>p]:m-0" dangerouslySetInnerHTML={{ __html: additionalContent }} />}
      </div>
    </section>
  );
};
export default PaymentMethods;
